import { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import SearchableSelect from '../components/SearchableSelect';
import { useCurrentUser } from '../context/UserContext';
import { buildStatusGroupMap, splitByStatusGroup } from '../utils/statusGroups';
import { getWeekStart, addWeeks, workweekLabel, toISODateString } from '../utils/weekDates';

const API = process.env.REACT_APP_API_URL;

// Hardcoded for v1 — confirmed with the user, not admin-editable lookup
// values (matches app/routers/week_board.py's ACTIVITY_TYPES exactly).
const ACTIVITIES = ['Client delivery', 'Supplier delivery', 'Scrub', 'Call reviews', 'Spinup', 'Other'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Tasks are color-coded by activity_type now, not by owner (2026-09-03,
// explicit ask — "instead of color coding owners, color code tasks").
// Client delivery/Supplier delivery/Scrub+Call reviews reuse the exact hues
// the old per-owner-role legend used (blue/green/purple), just re-purposed
// onto the task itself; Spinup/Other get no tint at all ("Other=white").
const ACTIVITY_COLORS = {
  'Client delivery':   { bg: '#e6f1fb', border: '#1a6da8', text: '#1a6da8' },
  'Supplier delivery':  { bg: '#e1f5ee', border: '#0f6e56', text: '#0f6e56' },
  'Scrub':             { bg: '#f3ebf8', border: '#7d3c98', text: '#7d3c98' },
  'Call reviews':      { bg: '#f3ebf8', border: '#7d3c98', text: '#7d3c98' },
};
const ACTIVITY_COLOR_DEFAULT = { bg: '#ffffff', border: '#ddd', text: '#555' }; // Spinup / Other
function activityColorFor(activityType) {
  return ACTIVITY_COLORS[activityType] || ACTIVITY_COLOR_DEFAULT;
}

// Plain, neutral owner/co-owner chip — no longer role-colored (see above).
const OWNER_CHIP = { color: '#555', bg: '#f1f2f4' };

// Row-highlight background (click-to-select, see selectedProgramId) — a
// warm amber distinct from every activity-tint color above so it reads as
// "this row is selected," not another task-type tint.
const ROW_HIGHLIGHT_BG = '#fef9e7';

function ownerKeyFor(userId, supplierId) {
  if (userId) return `user:${userId}`;
  if (supplierId) return `supplier:${supplierId}`;
  return null;
}

// Every owner-identity key on an item — primary AND co-owner (migration
// 0106) — so "View by" matches a person/supplier in either slot, not just
// the primary one.
function itemOwnerKeys(item) {
  return [
    ownerKeyFor(item.owner_user_id, item.owner_supplier_id),
    ownerKeyFor(item.co_owner_user_id, item.co_owner_supplier_id),
  ].filter(Boolean);
}

function ownerRoleFor(program, userId, supplierId) {
  if (supplierId) return 'supplier';
  if (userId) {
    if (userId === program.program_manager_id) return 'pm';
    if ((program.supporting_managers || []).some(u => u.id === userId)) return 'supporting';
    return 'admin';
  }
  return null;
}

// Any admin (Loria, Ed, ...) can be assigned to any activity, on top of the
// program's own PM/Supporting PM/Supplier — per explicit request. Skips an
// admin who's already listed as this program's PM/Supporting PM so nobody
// shows up twice under two different role labels.
function ownerOptionsForProgram(program, suppliers, admins) {
  const opts = [];
  if (program.program_manager_id) {
    opts.push({ value: `user:${program.program_manager_id}`, label: `${program.program_manager_name} (PM)` });
  }
  (program.supporting_managers || []).forEach(u => {
    opts.push({ value: `user:${u.id}`, label: `${u.name} (Supporting PM)` });
  });
  suppliers.forEach(s => {
    opts.push({ value: `supplier:${s.id}`, label: `${s.supplier_name} (Supplier)` });
  });
  admins.forEach(u => {
    const alreadyListed = u.id === program.program_manager_id
      || (program.supporting_managers || []).some(sm => sm.id === u.id);
    if (!alreadyListed) opts.push({ value: `user:${u.id}`, label: `${u.name} (Admin)` });
  });
  return opts;
}

// The one Supporting PM whose supporting_pm_focus (User.supporting_pm_focus,
// migration 0105 — 'scrub'/'call_review'/'both'/null, set on Admin's User
// Management form) lands in `focuses` — null if zero or more than one match,
// same "only when unambiguous" rule every branch here follows.
function focusedSupportingManagerId(supporting, focuses) {
  const matches = supporting.filter(u => focuses.has(u.supporting_pm_focus));
  return matches.length === 1 ? matches[0].id : null;
}

// Pre-designator fallback: exactly one Supporting PM AND nobody on this
// program has any focus set at all. An explicit focus on someone (even if
// it doesn't match this activity) means "no automatic guess," not "guess
// anyway" — mirrors week_board_owners.py's _legacy_single_supporting_
// manager_id() exactly.
function legacySingleSupportingManagerId(supporting) {
  return (supporting.length === 1 && !supporting.some(u => u.supporting_pm_focus)) ? supporting[0].id : null;
}

// Client/Supplier delivery -> PM/Supplier, Scrub -> the Supporting PM
// designated 'scrub'/'both', Call reviews -> the Supporting PM designated
// 'call_review'/'both' AND the resolved Supplier as co-owner when both are
// determinable (migration 0105 — the PM reviews the calls, the supplier
// fields them, both are real owners of that work) — only when the program
// has an unambiguous match (program.supplier_id is already null whenever
// the program's goals disagree on a supplier, see week_board.py). Spinup/
// Other have no canonical owner. Always just a normal owner assignment
// underneath — freely changeable afterward via the "+"/badge. Server-side
// mirror: app/utils/week_board_owners.py's resolve_matched_owner().
function matchedOwnerFor(program, activityType) {
  const supporting = program.supporting_managers || [];
  if (activityType === 'Client delivery' && program.program_manager_id) {
    return { owner_user_id: program.program_manager_id };
  }
  if (activityType === 'Scrub') {
    // Falls back to the primary PM when Supporting-PM resolution is
    // ambiguous (2026-09-02, real report — mirrors backend
    // week_board_owners.py's resolve_matched_owner(), see its docstring
    // for the full why) — otherwise an ambiguous program's Scrub item gets
    // no owner at all and never shows in anyone's My Home.
    const ownerId = focusedSupportingManagerId(supporting, new Set(['scrub', 'both']))
      || legacySingleSupportingManagerId(supporting)
      || program.program_manager_id;
    if (ownerId) return { owner_user_id: ownerId };
  }
  if (activityType === 'Call reviews') {
    const pmOwnerId = focusedSupportingManagerId(supporting, new Set(['call_review', 'both']))
      || legacySingleSupportingManagerId(supporting);
    if (pmOwnerId && program.supplier_id) {
      return { owner_user_id: pmOwnerId, co_owner_supplier_id: program.supplier_id };
    }
    if (pmOwnerId) return { owner_user_id: pmOwnerId };
    if (program.supplier_id) return { owner_supplier_id: program.supplier_id };
  }
  if (activityType === 'Supplier delivery' && program.supplier_id) {
    return { owner_supplier_id: program.supplier_id };
  }
  return null;
}

function matchedOwnerLabel(program, matched) {
  if (!matched) return null;
  if (matched.owner_supplier_id) return program.supplier_name;
  if (matched.owner_user_id === program.program_manager_id) return program.program_manager_name;
  return (program.supporting_managers || []).find(u => u.id === matched.owner_user_id)?.name || null;
}

function matchedCoOwnerLabel(program, matched) {
  if (!matched?.co_owner_supplier_id) return null;
  return program.supplier_name;
}

// One week in ms — used both to skip the flight's spin-up week (computePace())
// and to divide a flight's delivery window into weeks (weeklyTargetDefault()).
const SPINUP_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEK_MS = SPINUP_WEEK_MS;

// A plain "YYYY-MM-DD" as a local-calendar date. `new Date(iso)` parses it as
// UTC midnight, which is the previous evening in the US — "Dec 7" printed as
// "Dec 6" on this board (Commvault, 2026-11-02). Anything else falls through
// to the Date constructor.
export function parseLocalDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
}

// The dates a program is paced against: its GOALS' window when the goals
// carry one (the backend sends the earliest goal start / latest goal end),
// else the program's flight dates. A client can ask to front-load and finish
// early, so every goal ends well before the program does (Commvault Double
// Touch: program flight ends Dec 7, goals end Nov 13) — pacing against the
// program date understated how fast the work has to go.
export function paceDates(program) {
  return {
    start: program.goal_date_start || program.flight_date_start,
    end: program.goal_date_end || program.flight_date_end,
  };
}

// The flight's delivery window — everything after the spin-up week (week 1
// is always ramp-up, never lead delivery). Shared by computePace() and
// weeklyTargetDefault() so both agree on exactly the same window.
function deliveryWindow(program) {
  const { start: startIso, end: endIso } = paceDates(program);
  if (!startIso || !endIso) return null;
  const start = parseLocalDate(startIso);
  const end = parseLocalDate(endIso);
  const deliveryStart = new Date(start.getTime() + SPINUP_WEEK_MS);
  const totalMs = end - deliveryStart;
  if (totalMs <= 0) return null;
  return { deliveryStart, totalMs };
}

// Week 1 of a flight is always spinup, not lead delivery — the pace clock
// starts on day 8, and the whole delivery timeline is flight_date_end minus
// that first week. Still in week 1 -> trivially on pace (nothing's expected
// from delivery yet, that's not the same as no data to judge pace from).
//
// `riskScore` (2026-09-03, added for "order programs by pacing risk") is
// elapsedPct - acceptedPct — positive and growing the further a program has
// fallen behind where it should be, negative/small once it's ahead. Kept on
// the same object `onPace` already lives on rather than a second function,
// since both are derived from the exact same elapsedMs/totalMs/pctGoal —
// splitting them would just mean computing the window twice.
export function computePace(program) {
  if (!program.goal_count) return null;
  const window = deliveryWindow(program);
  if (!window) return null;
  const { deliveryStart, totalMs } = window;
  const now = new Date();
  const pctGoal = program.goal_count > 0 ? program.accepted_count / program.goal_count : 0;
  if (now < deliveryStart) return { onPace: true, riskScore: -1 };
  const elapsedMs = Math.min(now - deliveryStart, totalMs);
  const elapsedPct = elapsedMs / totalMs;
  return { onPace: pctGoal >= elapsedPct, riskScore: elapsedPct - pctGoal };
}

// Sort key for "order programs by pacing risk" — a program with no pace data
// at all (no goal, no flight window) has nothing to be at risk of yet, so it
// sorts to the bottom, same as one still in its spin-up week (riskScore -1);
// the most-behind-pace program sorts first. Ties (including the common
// "no data" case) keep a stable, predictable secondary order by name rather
// than whatever order the backend happened to return.
function pacingSortKey(program) {
  const pace = computePace(program);
  return pace ? pace.riskScore : -Infinity;
}
function sortByPacingRisk(programs) {
  return [...programs].sort((a, b) => {
    const diff = pacingSortKey(b) - pacingSortKey(a);
    return diff !== 0 ? diff : (a.program_name || '').localeCompare(b.program_name || '');
  });
}

// Suggested weekly pacing target when no explicit override is set
// (migration 0106, ProgramWeeklyTarget). Recomputed from REMAINING leads
// needed (goal_count minus accepted_count so far) over REMAINING delivery
// weeks (same window computePace() uses), not a static goal_count/totalWeeks
// split — a program that's behind or ahead of pace needs a different weekly
// number than week 1's plan, and the old static version showed the exact
// same figure every week regardless of progress (real ask, 2026-09-16: "the
// auto count weekly target recommendation should be updated weekly based on
// remaining leads needed in the timeframe"). Computed here, not stored
// server-side, so it stays correct automatically as accepted_count/goal_count/
// flight dates change — see backend CLAUDE.md's week_board router section.
// An explicit ProgramWeeklyTarget override (a PM's own number for one
// specific week) always wins over this — see the call site below.
export function weeklyTargetDefault(program) {
  if (!program.goal_count) return null;
  const window = deliveryWindow(program);
  if (!window) return null;
  const { deliveryStart, totalMs } = window;
  const now = new Date();
  const remainingLeads = Math.max(0, program.goal_count - (program.accepted_count || 0));
  const elapsedMs = Math.max(0, Math.min(now - deliveryStart, totalMs));
  const remainingWeeks = Math.max(1, Math.ceil((totalMs - elapsedMs) / WEEK_MS));
  return Math.round(remainingLeads / remainingWeeks);
}

function fmtDate(iso) {
  if (!iso) return null;
  return parseLocalDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Local-calendar-day math on a plain "YYYY-MM-DD" — builds the Date from its
// own y/m/d components (not `new Date(iso)`, which parses as UTC midnight
// and can roll to the wrong local day), same convention as weekDates.js.
function addDaysToIso(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

const inputStyle = { padding: '5px 7px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' };

export default function WeekAtAGlance() {
  const { currentUser } = useCurrentUser();
  // A supplier (2026-08-28 — previously no access at all) only ever gets a
  // read-only view: GET /week-board/programs already scopes their data to
  // their own owned/co-owned items server-side (a real access-model change,
  // not just this UI filter), but every write endpoint here stays admin/
  // program_manager-only, so a partner's edit/drag/delete controls would
  // just 403 — hidden entirely rather than shown-then-failing.
  const isReadOnly = currentUser?.role === 'partner';
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [boardPrograms, setBoardPrograms] = useState([]);
  const [weekDates, setWeekDates] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [viewBy, setViewBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addingCell, setAddingCell] = useState(null); // `${programId}:${dateIso}` | null
  const [editingOwnerItemId, setEditingOwnerItemId] = useState(null);
  const [editingCoOwnerItemId, setEditingCoOwnerItemId] = useState(null);
  const [editingWeeklyTargetProgramId, setEditingWeeklyTargetProgramId] = useState(null);
  const [editingWeeklyCommittedProgramId, setEditingWeeklyCommittedProgramId] = useState(null);
  const [editingNoteItemId, setEditingNoteItemId] = useState(null);
  // Programs not currently in the literal "WIP" status (Spinup, Pending
  // Kickoff, Closed – Not Invoiced, ...) collapse into "Other" by default —
  // still real, still-open programs (already-Closed ones stay excluded
  // entirely, unchanged), just not this week's forward-planning focus.
  const [showOther, setShowOther] = useState(false);
  // Click a program row to highlight it (2026-09-03, explicit ask — "for
  // visibility") — a plain toggle, purely visual, no persistence. Click the
  // same row again (or any other row) to clear/move the highlight.
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const dragRef = useRef(null); // { itemId, programId } while a drag is in progress

  useEffect(() => {
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
    axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data)).catch(() => {});
    axios.get(`${API}/users/?role=admin`).then(r => setAdmins(r.data)).catch(() => {});
  }, []);

  const loadBoard = () => {
    setLoading(true);
    setError('');
    axios.get(`${API}/week-board/programs`, { params: { week_start: toISODateString(weekStart) } })
      .then(r => {
        setBoardPrograms(r.data.programs);
        setWeekDates(r.data.week_dates);
      })
      .catch(() => setError('Failed to load the board.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBoard(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const { active: activePrograms } = splitByStatusGroup(
    boardPrograms, { statusKey: 'status', groupMap: programStatusGroupMap, dateKey: 'flight_date_end' }
  );

  const ownerFilterOptions = useMemo(() => {
    const seen = new Map();
    activePrograms.forEach(p => {
      Object.values(p.items || {}).flat().forEach(item => {
        const primaryKey = ownerKeyFor(item.owner_user_id, item.owner_supplier_id);
        const primaryName = item.owner_user_name || item.owner_supplier_name;
        if (primaryKey && primaryName) seen.set(primaryKey, primaryName);
        // Co-owner (migration 0105) is a real, separate owner identity too —
        // discoverable in "View by" the same as the primary slot.
        const coKey = ownerKeyFor(item.co_owner_user_id, item.co_owner_supplier_id);
        const coName = item.co_owner_user_name || item.co_owner_supplier_name;
        if (coKey && coName) seen.set(coKey, coName);
      });
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activePrograms]);

  // "View by" hides whole program rows with nothing assigned to that person
  // this week (in either owner slot), on top of the existing per-line dim
  // within a kept row.
  const visiblePrograms = viewBy
    ? activePrograms.filter(p => Object.values(p.items || {}).flat().some(item => itemOwnerKeys(item).includes(viewBy)))
    : activePrograms;

  // Ordered by pacing risk (2026-09-03, explicit ask) — most-behind-pace
  // program first in each bucket, so the rows most likely to need attention
  // are the ones a PM sees without scrolling.
  const wipVisiblePrograms = sortByPacingRisk(visiblePrograms.filter(p => p.status === 'WIP'));
  const otherVisiblePrograms = sortByPacingRisk(visiblePrograms.filter(p => p.status !== 'WIP'));

  const savePreferredDay = (programId, day) => {
    setBoardPrograms(prev => prev.map(p => p.id === programId ? { ...p, preferred_delivery_day: day || null } : p));
    axios.put(`${API}/programs/${programId}/preferred-delivery-day`, { day: day || null }).catch(() => loadBoard());
  };

  // Full replace of this program's pacing-target override for the
  // currently viewed week — {target_count: null, note: null} clears it back
  // to the computed default (weeklyTargetDefault()). See backend CLAUDE.md's
  // week_board router section.
  const saveWeeklyTarget = (programId, targetCount, note) => {
    setBoardPrograms(prev => prev.map(p => p.id === programId
      ? { ...p, weekly_target: { ...p.weekly_target, target_count: targetCount, note } } : p));
    setEditingWeeklyTargetProgramId(null);
    axios.put(`${API}/week-board/programs/${programId}/weekly-target`, { target_count: targetCount, note },
      { params: { week_start: toISODateString(weekStart) } }
    ).catch(() => { setError('Failed to save weekly target.'); loadBoard(); });
  };

  // Override for the computed "Weekly committed" figure (real ask,
  // 2026-09-16) — a separate row/endpoint from the weekly target above
  // (migration 0112) so saving one can never clobber the other.
  // {committed_override: null} clears it back to the computed sum of this
  // week's WeekBoardItem.expected_count.
  const saveWeeklyCommitted = (programId, committedOverride) => {
    setBoardPrograms(prev => prev.map(p => p.id === programId
      ? { ...p, weekly_target: { ...p.weekly_target, committed_override: committedOverride } } : p));
    setEditingWeeklyCommittedProgramId(null);
    axios.put(`${API}/week-board/programs/${programId}/weekly-committed`, { committed_override: committedOverride },
      { params: { week_start: toISODateString(weekStart) } }
    ).catch(() => { setError('Failed to save weekly committed.'); loadBoard(); });
  };

  const addItem = (programId, dateIso, activityType, note, matchedOwner) => {
    axios.post(`${API}/week-board/items`, {
      program_id: programId, activity_date: dateIso, activity_type: activityType, note: note || null,
      ...(matchedOwner || {}),
    }).then(() => { setAddingCell(null); loadBoard(); }).catch(() => setError('Failed to add item.'));
  };

  const deleteItem = (itemId) => {
    axios.delete(`${API}/week-board/items/${itemId}`).then(loadBoard).catch(() => setError('Failed to delete item.'));
  };

  const setItemOwner = (itemId, ownerValue) => {
    const body = ownerValue?.startsWith('user:')
      ? { owner_user_id: Number(ownerValue.slice(5)), owner_supplier_id: null }
      : ownerValue?.startsWith('supplier:')
        ? { owner_supplier_id: Number(ownerValue.slice(9)), owner_user_id: null }
        : { owner_user_id: null, owner_supplier_id: null };
    axios.patch(`${API}/week-board/items/${itemId}`, body)
      .then(() => { setEditingOwnerItemId(null); loadBoard(); })
      .catch(() => setError('Failed to assign owner.'));
  };

  // Co-owner (migration 0105) — same shape as setItemOwner, targeting the
  // second, independent owner slot. Mainly auto-populated on Call reviews
  // items, but freely editable/removable like the primary owner.
  const setItemCoOwner = (itemId, ownerValue) => {
    const body = ownerValue?.startsWith('user:')
      ? { co_owner_user_id: Number(ownerValue.slice(5)), co_owner_supplier_id: null }
      : ownerValue?.startsWith('supplier:')
        ? { co_owner_supplier_id: Number(ownerValue.slice(9)), co_owner_user_id: null }
        : { co_owner_user_id: null, co_owner_supplier_id: null };
    axios.patch(`${API}/week-board/items/${itemId}`, body)
      .then(() => { setEditingCoOwnerItemId(null); loadBoard(); })
      .catch(() => setError('Failed to assign co-owner.'));
  };

  const setItemNote = (itemId, note) => {
    axios.patch(`${API}/week-board/items/${itemId}`, { note: note || null })
      .then(() => { setEditingNoteItemId(null); loadBoard(); })
      .catch(() => setError('Failed to update note.'));
  };

  const setItemCompleted = (itemId, completed) => {
    axios.patch(`${API}/week-board/items/${itemId}`, { completed })
      .then(loadBoard)
      .catch(() => setError('Failed to update item.'));
  };

  // Drag-and-drop to another day (same program row only — dragRef carries
  // the source program id so a drop on a different program's cell no-ops),
  // and the explicit "push to next week" button (+7 days on the same day of
  // week, since next week's columns aren't on screen to drag onto).
  const moveItem = (itemId, newDateIso) => {
    axios.patch(`${API}/week-board/items/${itemId}`, { activity_date: newDateIso })
      .then(loadBoard)
      .catch(() => setError('Failed to move item.'));
  };

  const handleDropOnCell = (programId, dateIso) => {
    const dragged = dragRef.current;
    dragRef.current = null;
    if (!dragged || dragged.programId !== programId) return;
    moveItem(dragged.itemId, dateIso);
  };

  return (
    <div style={{ padding: '20px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Week at a Glance</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => setWeekStart(w => addWeeks(w, -1))}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', cursor: 'pointer' }}>‹</button>
            <span style={{ fontSize: '13px', color: '#555', minWidth: '130px', textAlign: 'center' }}>{workweekLabel(weekStart)}</span>
            <button onClick={() => setWeekStart(w => addWeeks(w, 1))}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', cursor: 'pointer' }}>›</button>
            <button onClick={() => setWeekStart(getWeekStart(new Date()))} title="Jump to the current week"
              style={{ fontSize: '12px', fontWeight: 600, padding: '5px 10px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', color: '#1a6da8', cursor: 'pointer', marginLeft: '2px' }}>
              Today
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12.5px', color: '#555', fontWeight: 500 }}>View by</label>
            <SearchableSelect value={viewBy} onChange={setViewBy}
              options={[{ value: '', label: 'All' }, ...ownerFilterOptions]} placeholder="All" />
          </div>
        </div>
      </div>

      {isReadOnly && (
        <div style={{ fontSize: '12.5px', color: '#9a6b12', marginBottom: '14px', padding: '8px 12px', background: '#fdf1de', border: '1px solid #f0dcae', borderRadius: '8px' }}>
          👁 Read-only view — showing only your own assigned/co-owned items. Add your expected deliveries from{' '}
          <strong>My Home</strong>.
        </div>
      )}

      {/* Legend now describes task colors, not owner-role colors (2026-09-03,
          explicit ask — tasks are color-coded by activity_type now, owners
          no longer are; see ACTIVITY_COLORS above). */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#555', marginBottom: '14px', padding: '8px 12px', background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px' }}>
        <span><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '2px', background: ACTIVITY_COLORS['Client delivery'].bg, border: `1px solid ${ACTIVITY_COLORS['Client delivery'].border}`, marginRight: '5px' }} />Client delivery</span>
        <span><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '2px', background: ACTIVITY_COLORS['Supplier delivery'].bg, border: `1px solid ${ACTIVITY_COLORS['Supplier delivery'].border}`, marginRight: '5px' }} />Supplier delivery</span>
        <span><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '2px', background: ACTIVITY_COLORS['Scrub'].bg, border: `1px solid ${ACTIVITY_COLORS['Scrub'].border}`, marginRight: '5px' }} />Scrub / Call reviews</span>
        <span><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '2px', background: ACTIVITY_COLOR_DEFAULT.bg, border: `1px solid ${ACTIVITY_COLOR_DEFAULT.border}`, marginRight: '5px' }} />Spinup / Other</span>
      </div>

      {error && <div style={{ color: '#c0392b', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}

      <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '1370px', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {/* Progress-to-goal folded into the Program column
                    (2026-09-03, explicit ask — "condense to show Mon-Fri...
                    move Progress to goal within program column") — one
                    fewer column frees real width for the 5 day columns,
                    which is where the actual work of this page lives. */}
                {['Program', ...DAYS].map((h, i) => (
                  <th key={h} style={{
                    background: '#2c3e50', color: 'white', fontSize: '11px', fontWeight: 600,
                    letterSpacing: '0.03em', textTransform: 'uppercase', textAlign: 'left', padding: '10px 12px',
                    width: i === 0 ? '270px' : '220px',
                    ...(i === 0 ? { position: 'sticky', left: 0, zIndex: 2, boxShadow: '2px 0 4px rgba(0,0,0,0.08)' } : {}),
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wipVisiblePrograms.map(program => (
                <ProgramRow
                  key={program.id}
                  program={program}
                  weekDates={weekDates}
                  isSelected={selectedProgramId === program.id}
                  onToggleSelected={() => setSelectedProgramId(prev => prev === program.id ? null : program.id)}
                  suppliers={suppliers}
                  admins={admins}
                  viewBy={viewBy}
                  isReadOnly={isReadOnly}
                  addingCell={addingCell}
                  setAddingCell={setAddingCell}
                  editingOwnerItemId={editingOwnerItemId}
                  setEditingOwnerItemId={setEditingOwnerItemId}
                  editingCoOwnerItemId={editingCoOwnerItemId}
                  setEditingCoOwnerItemId={setEditingCoOwnerItemId}
                  editingWeeklyTargetProgramId={editingWeeklyTargetProgramId}
                  setEditingWeeklyTargetProgramId={setEditingWeeklyTargetProgramId}
                  editingWeeklyCommittedProgramId={editingWeeklyCommittedProgramId}
                  setEditingWeeklyCommittedProgramId={setEditingWeeklyCommittedProgramId}
                  editingNoteItemId={editingNoteItemId}
                  setEditingNoteItemId={setEditingNoteItemId}
                  dragRef={dragRef}
                  onSavePreferredDay={savePreferredDay}
                  onSaveWeeklyTarget={saveWeeklyTarget}
                  onSaveWeeklyCommitted={saveWeeklyCommitted}
                  onAddItem={addItem}
                  onDeleteItem={deleteItem}
                  onSetItemOwner={setItemOwner}
                  onSetItemCoOwner={setItemCoOwner}
                  onSetItemNote={setItemNote}
                  onSetItemCompleted={setItemCompleted}
                  onMoveItem={moveItem}
                  onDropOnCell={handleDropOnCell}
                />
              ))}

              {otherVisiblePrograms.length > 0 && (
                <>
                  <tr>
                    <td colSpan={1 + DAYS.length} onClick={() => setShowOther(o => !o)}
                      style={{
                        cursor: 'pointer', background: '#f8f9fa', padding: '9px 14px',
                        borderTop: '1px solid #eee', borderBottom: showOther ? '1px solid #eee' : 'none',
                      }}>
                      <span style={{ fontSize: '11px', color: '#888', marginRight: '8px' }}>{showOther ? '▼' : '▶'}</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>Other</span>
                      <span style={{ background: '#e8ecef', color: '#555', borderRadius: '10px', padding: '1px 8px', fontSize: '11px', marginLeft: '8px' }}>
                        {otherVisiblePrograms.length}
                      </span>
                      <span style={{ fontSize: '11.5px', color: '#999', marginLeft: '10px' }}>
                        Not currently WIP — Spinup, Pending Kickoff, Closed – Not Invoiced, etc.
                      </span>
                    </td>
                  </tr>
                  {showOther && otherVisiblePrograms.map(program => (
                    <ProgramRow
                      key={program.id}
                      program={program}
                      weekDates={weekDates}
                      isSelected={selectedProgramId === program.id}
                      onToggleSelected={() => setSelectedProgramId(prev => prev === program.id ? null : program.id)}
                      suppliers={suppliers}
                      admins={admins}
                      viewBy={viewBy}
                      isReadOnly={isReadOnly}
                      addingCell={addingCell}
                      setAddingCell={setAddingCell}
                      editingOwnerItemId={editingOwnerItemId}
                      setEditingOwnerItemId={setEditingOwnerItemId}
                      editingCoOwnerItemId={editingCoOwnerItemId}
                      setEditingCoOwnerItemId={setEditingCoOwnerItemId}
                      editingWeeklyTargetProgramId={editingWeeklyTargetProgramId}
                      setEditingWeeklyTargetProgramId={setEditingWeeklyTargetProgramId}
                      editingNoteItemId={editingNoteItemId}
                      setEditingNoteItemId={setEditingNoteItemId}
                      dragRef={dragRef}
                      onSavePreferredDay={savePreferredDay}
                      onSaveWeeklyTarget={saveWeeklyTarget}
                  onSaveWeeklyCommitted={saveWeeklyCommitted}
                      onAddItem={addItem}
                      onDeleteItem={deleteItem}
                      onSetItemOwner={setItemOwner}
                      onSetItemCoOwner={setItemCoOwner}
                      onSetItemNote={setItemNote}
                      onSetItemCompleted={setItemCompleted}
                      onMoveItem={moveItem}
                      onDropOnCell={handleDropOnCell}
                    />
                  ))}
                </>
              )}

              {!loading && activePrograms.length === 0 && (
                <tr><td colSpan={1 + DAYS.length} style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: '13px' }}>No active programs.</td></tr>
              )}
              {!loading && activePrograms.length > 0 && visiblePrograms.length === 0 && (
                <tr><td colSpan={1 + DAYS.length} style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                  No programs have activity assigned to {ownerFilterOptions.find(o => o.value === viewBy)?.label || 'that person'} this week.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgramRow({
  program, weekDates, suppliers, admins, viewBy, isReadOnly, addingCell, setAddingCell,
  isSelected, onToggleSelected,
  editingOwnerItemId, setEditingOwnerItemId, editingCoOwnerItemId, setEditingCoOwnerItemId,
  editingWeeklyTargetProgramId, setEditingWeeklyTargetProgramId,
  editingWeeklyCommittedProgramId, setEditingWeeklyCommittedProgramId,
  editingNoteItemId, setEditingNoteItemId, dragRef,
  onSavePreferredDay, onSaveWeeklyTarget, onSaveWeeklyCommitted, onAddItem, onDeleteItem, onSetItemOwner, onSetItemCoOwner, onSetItemNote, onSetItemCompleted, onMoveItem, onDropOnCell,
}) {
  const pace = computePace(program);
  const pct = program.goal_count > 0 ? Math.min(100, Math.round((program.accepted_count / program.goal_count) * 100)) : 0;
  const weeklyTarget = program.weekly_target || {};
  const isTargetOverridden = weeklyTarget.target_count != null;
  const displayedTarget = isTargetOverridden ? weeklyTarget.target_count : weeklyTargetDefault(program);
  const isEditingWeeklyTarget = editingWeeklyTargetProgramId === program.id;
  // "Weekly committed" (real ask, 2026-09-16) — sum of what suppliers have
  // logged as their own expected-delivery estimate for this visible week
  // (WeekBoardItem.expected_count, only ever set via POST /supplier-items'
  // "Add This Week's Expected Deliveries" flow, activity_type='Supplier
  // delivery') — computed client-side since GET /week-board/programs
  // already returns every item for the week, same as every other
  // client-side aggregation in this file (e.g. ownerFilterOptions). A PM can
  // override this computed sum (migration 0112) the same way the weekly
  // target itself is overridable, in case a supplier's logged estimate is
  // stale/wrong or nothing was logged for a delivery known some other way.
  const computedWeeklyCommitted = weekDates.reduce((sum, dateIso) => {
    const items = (program.items && program.items[dateIso]) || [];
    return sum + items.reduce((s, item) => (
      item.activity_type === 'Supplier delivery' && item.expected_count != null ? s + item.expected_count : s
    ), 0);
  }, 0);
  const isCommittedOverridden = weeklyTarget.committed_override != null;
  const displayedCommitted = isCommittedOverridden ? weeklyTarget.committed_override : computedWeeklyCommitted;
  const isEditingWeeklyCommitted = editingWeeklyCommittedProgramId === program.id;
  // Click a program row to highlight it (2026-09-03, explicit ask) — the
  // sticky first column paints its own background (position:sticky needs an
  // opaque background of its own or content scrolled underneath shows
  // through), so it needs the highlight color explicitly; every other cell
  // just inherits the <tr>'s.
  const rowBg = isSelected ? ROW_HIGHLIGHT_BG : undefined;

  return (
    <tr onClick={onToggleSelected} style={{ borderBottom: '1px solid #eee', background: rowBg, cursor: 'pointer' }}>
      {/* Program name/client/delivery-day + Progress-to-goal, folded into
          one condensed column (2026-09-03, explicit ask — "move Progress to
          goal within program column and condense text in program column...
          trying to avoid busy UI") — was two separate columns. */}
      <td style={{
        padding: '9px 12px', verticalAlign: 'top',
        position: 'sticky', left: 0, zIndex: 1, background: rowBg || 'white', boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>{program.program_name}</div>
        <div style={{ fontSize: '11px', color: '#999', marginTop: '1px' }}>{program.client_name}</div>
        <div style={{ marginTop: '4px', fontSize: '11px', color: '#555', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '3px 4px' }} onClick={e => e.stopPropagation()}>
          Delivery day:
          {isReadOnly ? (
            <strong>{program.preferred_delivery_day || '—'}</strong>
          ) : (
            <select value={program.preferred_delivery_day || ''} onChange={e => onSavePreferredDay(program.id, e.target.value)}
              style={{ fontSize: '11px', padding: '1px 3px', borderRadius: '4px', border: '1px solid #ddd', background: '#f9f9f9', color: '#555' }}>
              <option value="">—</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>

        <div style={{ marginTop: '7px', paddingTop: '7px', borderTop: '1px solid #f0f0f0' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '11.5px', marginBottom: '4px' }}>
            <span style={{ fontWeight: 600, color: '#2c3e50' }}>{program.accepted_count} / {program.goal_count}{program.goal_count_is_fallback ? ' (est.)' : ''}</span>
            <span
              style={{ color: '#999', fontSize: '10.5px' }}
              title={program.goal_date_end && program.flight_date_end && program.goal_date_end !== program.flight_date_end
                ? `Goal dates end ${fmtDate(program.goal_date_end)}; the program's flight runs to ${fmtDate(program.flight_date_end)}. Pace and the weekly target use the goal dates.`
                : undefined}
            >{paceDates(program).end ? `goal ${fmtDate(paceDates(program).end)}` : ''}</span>
          </div>
          <div style={{ height: '7px', borderRadius: '4px', background: '#eee', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: '4px', width: `${pct}%`, background: pace ? (pace.onPace ? '#27ae60' : '#b7791f') : '#999' }} />
          </div>
          {pace && (
            <div style={{ fontSize: '10.5px', marginTop: '3px', color: pace.onPace ? '#27ae60' : '#b7791f' }}>
              {pace.onPace ? 'On pace' : 'Behind pace'}
            </div>
          )}
          {/* Weekly pacing target (migration 0106) — an adjustable per-week
              number + note. Falls back to weeklyTargetDefault() (goal spread
              across delivery weeks) when no explicit override exists for
              this week. */}
          <div style={{ marginTop: '5px' }}>
            {isEditingWeeklyTarget ? (
              <WeeklyTargetEditor
                initialTarget={weeklyTarget.target_count}
                initialNote={weeklyTarget.note}
                onSave={(targetCount, note) => onSaveWeeklyTarget(program.id, targetCount, note)}
                onCancel={() => setEditingWeeklyTargetProgramId(null)}
              />
            ) : (
              <div
                onClick={() => !isReadOnly && setEditingWeeklyTargetProgramId(program.id)}
                title={isReadOnly ? undefined : "Click to adjust this week's pacing target"}
                style={{ fontSize: '10.5px', color: '#777', display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap', cursor: isReadOnly ? 'default' : 'pointer' }}
              >
                <span>
                  Weekly target: <strong style={{ color: '#2c3e50' }}>{displayedTarget != null ? displayedTarget : '—'}</strong>
                  {!isTargetOverridden && displayedTarget != null && <span style={{ color: '#bbb' }}> (auto)</span>}
                </span>
                {weeklyTarget.note && <span style={{ color: '#999' }}>— {weeklyTarget.note}</span>}
                {!isReadOnly && <span style={{ color: '#bbb' }}>✎</span>}
              </div>
            )}
          </div>
          {/* Real ask, 2026-09-16 — let a PM see at a glance whether what
              suppliers said they'd deliver this week (Weekly committed) and
              what actually landed are tracking the weekly target above.
              Weekly committed is editable (migration 0112, same
              override-over-a-computed-default pattern as the weekly target
              itself) — a PM can correct it when a supplier's logged estimate
              is stale/wrong or nothing was logged at all.
              Split into two numbers, 2026-09-21 (real bug report) — this was
              one "Total delivered this week" counting only SupplierDelivery
              rows uploaded within this Mon-Fri window (supplier -> NeTrA),
              which read 0 for a program that had genuinely delivered leads
              to the CLIENT this week, since those leads' supplier upload
              could have landed weeks earlier. Now shown as two separate
              lines: Supplier delivered (unchanged query) and Client
              delivered (new — Lead.client_delivery_date within the same
              window), so a PM can actually see both directions. */}
          <div style={{ marginTop: '3px' }}>
            {isEditingWeeklyCommitted ? (
              <WeeklyCommittedEditor
                initialValue={weeklyTarget.committed_override}
                onSave={(value) => onSaveWeeklyCommitted(program.id, value)}
                onCancel={() => setEditingWeeklyCommittedProgramId(null)}
              />
            ) : (
              <div
                onClick={() => !isReadOnly && setEditingWeeklyCommittedProgramId(program.id)}
                title={isReadOnly ? undefined : "Click to override this week's committed count"}
                style={{ fontSize: '10.5px', color: '#777', display: 'flex', alignItems: 'baseline', gap: '4px', cursor: isReadOnly ? 'default' : 'pointer' }}
              >
                <span>
                  Weekly committed: <strong style={{ color: '#2c3e50' }}>{displayedCommitted}</strong>
                  {isCommittedOverridden && <span style={{ color: '#bbb' }}> (override)</span>}
                </span>
                {!isReadOnly && <span style={{ color: '#bbb' }}>✎</span>}
              </div>
            )}
          </div>
          <div style={{ fontSize: '10.5px', color: '#777', marginTop: '3px' }}>
            Supplier delivered this week: <strong style={{ color: '#2c3e50' }}>{program.supplier_delivered_this_week ?? 0}</strong>
          </div>
          <div style={{ fontSize: '10.5px', color: '#777' }}>
            Client delivered this week: <strong style={{ color: '#2c3e50' }}>{program.client_delivered_this_week ?? 0}</strong>
          </div>
        </div>
      </td>
      {weekDates.map(dateIso => (
        <DayCell
          key={dateIso}
          program={program}
          dateIso={dateIso}
          suppliers={suppliers}
          admins={admins}
          viewBy={viewBy}
          isReadOnly={isReadOnly}
          isAdding={addingCell === `${program.id}:${dateIso}`}
          onOpenAdd={() => setAddingCell(`${program.id}:${dateIso}`)}
          onCloseAdd={() => setAddingCell(null)}
          editingOwnerItemId={editingOwnerItemId}
          setEditingOwnerItemId={setEditingOwnerItemId}
          editingCoOwnerItemId={editingCoOwnerItemId}
          setEditingCoOwnerItemId={setEditingCoOwnerItemId}
          editingNoteItemId={editingNoteItemId}
          setEditingNoteItemId={setEditingNoteItemId}
          dragRef={dragRef}
          onAddItem={(activityType, note, matchedOwner) => onAddItem(program.id, dateIso, activityType, note, matchedOwner)}
          onDeleteItem={onDeleteItem}
          onSetItemOwner={onSetItemOwner}
          onSetItemCoOwner={onSetItemCoOwner}
          onSetItemNote={onSetItemNote}
          onSetItemCompleted={onSetItemCompleted}
          onMoveItem={onMoveItem}
          onDropOnCell={() => onDropOnCell(program.id, dateIso)}
        />
      ))}
    </tr>
  );
}

function DayCell({
  program, dateIso, suppliers, admins, viewBy, isReadOnly, isAdding, onOpenAdd, onCloseAdd,
  editingOwnerItemId, setEditingOwnerItemId, editingCoOwnerItemId, setEditingCoOwnerItemId,
  editingNoteItemId, setEditingNoteItemId, dragRef,
  onAddItem, onDeleteItem, onSetItemOwner, onSetItemCoOwner, onSetItemNote, onSetItemCompleted, onMoveItem, onDropOnCell,
}) {
  const allItems = (program.items && program.items[dateIso]) || [];
  // "View by" hides non-matching items outright (2026-08-28) — was dimming
  // them in place (opacity 0.32), but a filter that still shows everyone
  // else's items just faded isn't actually narrowing the view, and reads as
  // a privacy/scoping half-measure once suppliers/Supporting PMs can be on
  // this page at all. visiblePrograms (above) already hides whole rows with
  // nothing matching; this is the same filter applied within a row that
  // has some.
  const items = viewBy ? allItems.filter(item => itemOwnerKeys(item).includes(viewBy)) : allItems;
  return (
    <td style={{ padding: '10px 12px', verticalAlign: 'top', height: '1px' }}>
      <div
        style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '32px' }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); onDropOnCell(); }}
      >
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '5px' }} onClick={e => e.stopPropagation()}>
          {items.map(item => {
            const role = ownerRoleFor(program, item.owner_user_id, item.owner_supplier_id);
            const coRole = ownerRoleFor(program, item.co_owner_user_id, item.co_owner_supplier_id);
            // Task color, not owner color (2026-09-03, explicit ask) — see
            // ACTIVITY_COLORS above. Owner/co-owner chips below are now a
            // single flat neutral style (OWNER_CHIP) regardless of role.
            const activityColor = activityColorFor(item.activity_type);
            return (
              <li key={item.id} draggable={!isReadOnly}
                onDragStart={() => { dragRef.current = { itemId: item.id, programId: program.id }; }}
                onDragEnd={() => { dragRef.current = null; }}
                title={isReadOnly ? undefined : 'Drag to another day'}
                style={{
                  display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12.5px',
                  background: activityColor.bg, borderLeft: `3px solid ${activityColor.border}`,
                  borderRadius: '4px', padding: '2px 4px 2px 6px',
                  cursor: isReadOnly ? 'default' : 'grab',
                }}>
                {/* Row 1: checkbox + activity/note — gets the cell's full width so a
                    long note wraps within it instead of fighting owner/» /× for room
                    (those move to row 2 below, see 2026-08-27 layout fix). Completing
                    an item no longer strikes through or dims the text (2026-09-03,
                    explicit ask — "only show check box complete") — the checkbox
                    itself is the only completion signal now. */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <input type="checkbox" checked={!!item.completed} title="Mark completed"
                    disabled={isReadOnly}
                    onChange={e => onSetItemCompleted(item.id, e.target.checked)}
                    style={{ marginTop: '2px', cursor: isReadOnly ? 'default' : 'pointer', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', color: '#2c3e50' }}>
                    {/* Activity label stays at its current size (2026-09-03,
                        explicit ask); only the note text shrinks. */}
                    <span style={{ fontWeight: 600, color: activityColor.text }}>{item.activity_type}</span>
                    {' '}
                    {editingNoteItemId === item.id ? (
                      <NoteEditor initialValue={item.note || ''} onSave={v => onSetItemNote(item.id, v)} onCancel={() => setEditingNoteItemId(null)} />
                    ) : item.note ? (
                      isReadOnly
                        ? <span style={{ color: '#666', fontSize: '11px' }}>— {item.note}</span>
                        : <span style={{ color: '#666', fontSize: '11px', cursor: 'pointer' }} onClick={() => setEditingNoteItemId(item.id)} title="Edit note">— {item.note}</span>
                    ) : (
                      // "note"/"co-owner" wording dropped in favor of a bare "+"
                      // everywhere on this row (2026-09-03, explicit ask) — the
                      // hover title still says what it does.
                      !isReadOnly && <span style={{ color: '#bbb', cursor: 'pointer', fontSize: '11px' }} onClick={() => setEditingNoteItemId(item.id)} title="Add note">+</span>
                    )}
                  </span>
                </div>
                {/* Row 2: owner + co-owner + push-to-next-week + remove — indented
                    under the note text, wraps freely since it's no longer sharing
                    a row with it. Co-owner (migration 0105) is a second, optional
                    slot — same edit affordance as the primary owner, just smaller. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', paddingLeft: '23px' }}>
                  {editingOwnerItemId === item.id ? (
                    <select autoFocus defaultValue={item.owner_user_id ? `user:${item.owner_user_id}` : item.owner_supplier_id ? `supplier:${item.owner_supplier_id}` : ''}
                      onChange={e => onSetItemOwner(item.id, e.target.value)}
                      onBlur={() => setEditingOwnerItemId(null)}
                      style={{ fontSize: '11px', padding: '1px 3px', borderRadius: '4px', maxWidth: '100%' }}>
                      <option value="">— choose —</option>
                      {ownerOptionsForProgram(program, suppliers, admins).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : role ? (
                    <button onClick={() => !isReadOnly && setEditingOwnerItemId(item.id)} title={isReadOnly ? undefined : 'Change owner'} disabled={isReadOnly}
                      style={{
                        flexShrink: 0, border: 'none', cursor: isReadOnly ? 'default' : 'pointer', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                        padding: '1px 7px', color: OWNER_CHIP.color, background: OWNER_CHIP.bg,
                      }}>{item.owner_user_name || item.owner_supplier_name}</button>
                  ) : !isReadOnly ? (
                    <button onClick={() => setEditingOwnerItemId(item.id)} title="Add owner" aria-label="Add owner"
                      style={{ flexShrink: 0, border: '1px dashed #ccc', cursor: 'pointer', borderRadius: '4px', fontSize: '11px', fontWeight: 600, padding: '1px 7px', color: '#999', background: '#f9f9f9' }}>+</button>
                  ) : null}

                  {editingCoOwnerItemId === item.id ? (
                    <select autoFocus defaultValue={item.co_owner_user_id ? `user:${item.co_owner_user_id}` : item.co_owner_supplier_id ? `supplier:${item.co_owner_supplier_id}` : ''}
                      onChange={e => onSetItemCoOwner(item.id, e.target.value)}
                      onBlur={() => setEditingCoOwnerItemId(null)}
                      style={{ fontSize: '11px', padding: '1px 3px', borderRadius: '4px', maxWidth: '100%' }}>
                      <option value="">— no co-owner —</option>
                      {ownerOptionsForProgram(program, suppliers, admins).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : coRole ? (
                    <button onClick={() => !isReadOnly && setEditingCoOwnerItemId(item.id)} title={isReadOnly ? undefined : 'Change co-owner'} disabled={isReadOnly}
                      style={{
                        flexShrink: 0, border: '1px dashed #ccc', cursor: isReadOnly ? 'default' : 'pointer', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                        padding: '1px 7px', color: OWNER_CHIP.color, background: OWNER_CHIP.bg,
                      }}>+ {item.co_owner_user_name || item.co_owner_supplier_name}</button>
                  ) : (!isReadOnly && role) ? (
                    <button onClick={() => setEditingCoOwnerItemId(item.id)} title="Add co-owner" aria-label="Add co-owner"
                      style={{ flexShrink: 0, border: '1px dashed #ccc', cursor: 'pointer', borderRadius: '4px', fontSize: '11px', fontWeight: 600, padding: '1px 6px', color: '#bbb', background: '#f9f9f9' }}>+</button>
                  ) : null}

                  {!isReadOnly && (
                    <>
                      <button onClick={() => onMoveItem(item.id, addDaysToIso(dateIso, 7))} title="Push to next week" aria-label="Push to next week"
                        style={{ flexShrink: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#bbb', fontSize: '12px', padding: '0 2px' }}>»</button>
                      <button onClick={() => onDeleteItem(item.id)} title="Remove" aria-label="Remove item"
                        style={{ flexShrink: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: '13px', padding: '0 2px' }}>×</button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {!isReadOnly && (
          <div style={{ marginTop: 'auto', paddingTop: '8px' }} onClick={e => e.stopPropagation()}>
            {isAdding ? (
              <AddItemForm program={program} onAdd={onAddItem} onCancel={onCloseAdd} />
            ) : (
              <button onClick={onOpenAdd} title="Add an item" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '12px', fontWeight: 500, padding: '2px 0' }}>+</button>
            )}
          </div>
        )}
      </div>
    </td>
  );
}

function NoteEditor({ initialValue, onSave, onCancel }) {
  const [value, setValue] = useState(initialValue);
  const cancelledRef = useRef(false);
  return (
    <input autoFocus type="text" value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={() => { if (cancelledRef.current) onCancel(); else onSave(value.trim()); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); cancelledRef.current = true; e.target.blur(); }
      }}
      style={{ fontSize: '12px', padding: '2px 4px', border: '1px solid #1a6da8', borderRadius: '4px', width: '150px' }} />
  );
}

function WeeklyTargetEditor({ initialTarget, initialNote, onSave, onCancel }) {
  const [target, setTarget] = useState(initialTarget != null ? String(initialTarget) : '');
  const [note, setNote] = useState(initialNote || '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <input autoFocus type="number" min="0" value={target} onChange={e => setTarget(e.target.value)}
        placeholder="Weekly target" style={{ ...inputStyle, width: '100px' }} />
      <input type="text" value={note} onChange={e => setNote(e.target.value)}
        placeholder="Note (optional)" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={() => onSave(target === '' ? null : Number(target), note.trim() || null)}
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #1a6da8', background: '#1a6da8', color: 'white' }}>
          Save
        </button>
        <button onClick={() => onSave(null, null)} title="Clear override — use the computed default"
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#555' }}>
          Reset
        </button>
        <button onClick={onCancel}
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#555' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Real ask, 2026-09-16 — "Weekly committed" (the sum of supplier-logged
// expected-delivery counts) is editable the same way the weekly target
// itself is, but with no note field — just a number override on top of the
// computed sum (migration 0112).
function WeeklyCommittedEditor({ initialValue, onSave, onCancel }) {
  const [value, setValue] = useState(initialValue != null ? String(initialValue) : '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <input autoFocus type="number" min="0" value={value} onChange={e => setValue(e.target.value)}
        placeholder="Weekly committed" style={{ ...inputStyle, width: '100px' }} />
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={() => onSave(value === '' ? null : Number(value))}
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #1a6da8', background: '#1a6da8', color: 'white' }}>
          Save
        </button>
        <button onClick={() => onSave(null)} title="Clear override — use the computed sum"
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#555' }}>
          Reset
        </button>
        <button onClick={onCancel}
          style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#555' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddItemForm({ program, onAdd, onCancel }) {
  const [activityType, setActivityType] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const matched = activityType ? matchedOwnerFor(program, activityType) : null;
  const matchedLabel = matchedOwnerLabel(program, matched);
  const matchedCoLabel = matchedCoOwnerLabel(program, matched);

  const confirm = () => {
    if (!activityType) { setErr('Pick an activity first.'); return; }
    onAdd(activityType, note.trim(), matchedOwnerFor(program, activityType));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <select value={activityType} onChange={e => { setActivityType(e.target.value); setErr(''); }} style={{ ...inputStyle, width: '100%' }}>
        <option value="">— activity —</option>
        {ACTIVITIES.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Add context (optional)" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
      {matchedLabel && <div style={{ fontSize: '11px', color: '#999' }}>Owner: {matchedLabel}{matchedCoLabel ? ` + ${matchedCoLabel} (co-owner)` : ''}</div>}
      {err && <div style={{ fontSize: '11px', color: '#c0392b' }}>{err}</div>}
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={confirm} style={{ fontSize: '11.5px', fontWeight: 600, borderRadius: '5px', padding: '3px 9px', cursor: 'pointer', border: '1px solid #1a6da8', background: '#1a6da8', color: 'white' }}>Add</button>
        <button onClick={onCancel} style={{ fontSize: '11.5px', fontWeight: 600, borderRadius: '5px', padding: '3px 9px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', color: '#555' }}>Cancel</button>
      </div>
    </div>
  );
}
